import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const Select = ({ 
  label, 
  value, 
  onChange, 
  options, 
  placeholder = 'Select an option',
  error, 
  required = false,
  disabled = false,
  className = '',
  responsive = false,
  size = 'md',
  ...props 
}) => {
  const { isMobile, getResponsiveValue, getComponentSize } = useTheme();

  // Get responsive size configuration
  const responsiveSize = responsive ? getComponentSize('input', size) : null;

  const labelSize = getResponsiveValue({
    mobile: 'text-sm',
    desktop: 'text-sm'
  });

  const selectPadding = responsiveSize 
    ? `p-[${responsiveSize.padding}]`
    : getResponsiveValue({
        mobile: 'px-3 py-3',
        desktop: 'px-4 py-3'
      });

  const selectFontSize = responsiveSize
    ? `text-[${responsiveSize.fontSize}]`
    : getResponsiveValue({
        mobile: 'text-base', // 16px to prevent zoom on iOS
        desktop: 'text-sm'
      });

  const selectHeight = responsiveSize
    ? `h-[${responsiveSize.height}]`
    : getResponsiveValue({
        mobile: 'min-h-[44px]', // Touch-friendly minimum
        desktop: 'h-auto'
      });

  const arrowSize = getResponsiveValue({
    mobile: 'w-4 h-4',
    desktop: 'w-5 h-5'
  });

  const arrowPadding = getResponsiveValue({
    mobile: 'pr-2.5',
    desktop: 'pr-3'
  });

  const errorIconSize = getResponsiveValue({
    mobile: 'w-4 h-4',
    desktop: 'w-4 h-4'
  });

  const errorTextSize = getResponsiveValue({
    mobile: 'text-sm',
    desktop: 'text-sm'
  });

  return (
    <div className="flex flex-col space-y-2">
      {label && (
        <label className={`${labelSize} font-medium text-text-primary`}>
          {label}
          {required && <span className="text-error ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        <select
          value={value}
          onChange={onChange}
          disabled={disabled}
          className={`
            w-full border border-border-primary rounded-xl bg-surface-primary
            focus:outline-none focus:ring-2 focus:ring-border-focus focus:border-border-focus
            text-text-primary appearance-none cursor-pointer
            disabled:bg-surface-secondary disabled:text-text-disabled disabled:cursor-not-allowed
            transition-colors duration-200
            ${selectPadding}
            ${selectFontSize}
            ${selectHeight}
            ${error ? 'border-border-error focus:ring-border-error focus:border-border-error' : ''}
            ${isMobile ? 'rounded-xl' : 'rounded-xl'}
            ${className}
          `}
          {...props}
        >
          <option value="" className="text-text-tertiary">
            {placeholder}
          </option>
          {options.map((option, index) => {
            // FIXED: Generate unique key to avoid React duplicate key warnings
            // Use option.value when it's unique, otherwise fall back to index
            const uniqueKey = option.value !== '' ? option.value : `empty-option-${index}`;
            
            return (
              <option 
                key={uniqueKey}
                value={option.value} 
                className="text-text-primary bg-surface-primary"
              >
                {option.label}
              </option>
            );
          })}
        </select>
        
        {/* Custom dropdown arrow */}
        <div className={`absolute inset-y-0 right-0 flex items-center ${arrowPadding} pointer-events-none`}>
          <svg className={`${arrowSize} text-text-tertiary`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      
      {error && (
        <span className={`${errorTextSize} text-error flex items-center`}>
          <svg className={`${errorIconSize} mr-1 flex-shrink-0`} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span className={isMobile ? 'break-words' : ''}>{error}</span>
        </span>
      )}
    </div>
  );
};

export default Select;